import React from 'react';
import useMortgage from "../../../../providers/mortgage/useMortgage";
import {Checkbox, Input, Table, Typography} from "antd";
import dayjs from "dayjs";
import {gray, red} from "@ant-design/colors";
import {useUploadAnnotator} from "../../../../providers/mortgage/uploads/UploadAnnotatorContext";
import {CloseCircleOutlined} from "@ant-design/icons";
import ArrowedNumber from "../../../_new/ui/numbers/ArrowedNumber";

const MONTHS_REQUIRED = 6
export function SummaryField({value, label, arrow}) {

    let icon = arrow ? parseFloat(value) > 0 ? '↑' : parseFloat(value) < 0 ? '↓' : null : null
    const inputProps = {}
    if (icon) {
        inputProps.prefix = <span className={parseFloat(value) > 0 ? "text-success" : "text-danger"}>{icon}</span>
    }

    return (
        <div style={{marginRight: -12}}>
            <Input readOnly value={value} addonBefore={label}
                   style={{textAlign: 'right', width: 150}} {...inputProps}/>
        </div>
    )
}


export function bankStatementSummary(mortgage){
    const allSections = mortgage.uploadSections
    const sections = allSections.filter(it => it.isActive && it.stage === 'APPLY' && it.category === 'bank-statements' )

    const threeWeeksAgo = dayjs().subtract(3, 'week');
    let fixedEndDate
    if (mortgage.requirement.statementAlignmentDate) {
        fixedEndDate = dayjs(mortgage.requirement.statementAlignmentDate, "YYYY-MM-DD").local().startOf('day')
    } else {
        let oldestRecent = null;

        for (const section of sections) {
            if (!section.summary?.range?.to?.date) continue;
            const toDate = section.summary.range.to.date;
            if (toDate.isAfter(threeWeeksAgo)) {
                if (!oldestRecent || toDate.isBefore(oldestRecent)) {
                    oldestRecent = toDate;
                }
            }
        }
        fixedEndDate = oldestRecent ? oldestRecent : threeWeeksAgo;
    }
    const requiredRange = {end: fixedEndDate, start: fixedEndDate.add(1, 'day').subtract(MONTHS_REQUIRED, 'month')};

    const currencyAccounts = {}
    sections.forEach(section=>{
        if (section.isActive) {
            const currency = section.currency
            const isEuro = !currency || currency.toLowerCase().trim().startsWith('eur') || currency.trim() === '€'
            const myCurrency = isEuro ? '€' : currency;
            if (!currencyAccounts[myCurrency]) {
                currencyAccounts[myCurrency] = {accounts: []}
                if (!isEuro){
                    currencyAccounts[myCurrency].accountingFx = mortgage.requirement.accountingFx[myCurrency] ? mortgage.requirement.accountingFx[myCurrency] : 1
                }
            }
            const finances = section.financesInRange(requiredRange.start, requiredRange.end);

            currencyAccounts[myCurrency].accounts.push(finances)
        }
    })

    Object.keys(currencyAccounts).forEach(key=> {
        currencyAccounts[key].totalBalance = 0
        currencyAccounts[key].totalTotal = 0
        currencyAccounts[key].totalAverage = 0
        let accountingFx = currencyAccounts[key].accountingFx
        if (accountingFx) {
            currencyAccounts[key].convertedTotalBalance = 0
            currencyAccounts[key].convertedTotalTotal = 0
            currencyAccounts[key].convertedTotalAverage = 0
        }
        currencyAccounts[key].accounts.forEach(({closingOnDate, total: totalAdjusted, averageSavings, includeInCalculations, ...rest}) => {
            const closingBalance = closingOnDate && closingOnDate.balance != null ? closingOnDate.balance : 0
            currencyAccounts[key].totalBalance += closingBalance
            currencyAccounts[key].totalTotal += totalAdjusted != null ? totalAdjusted : 0;
            const rate = Number(currencyAccounts[key]?.accountingFx || 1)
            if (accountingFx) {
                const convertedBalance = closingBalance / rate
                const balanceOnLastDate = Number(convertedBalance.toFixed(2))
                const convertedTotal = totalAdjusted != null ? totalAdjusted / rate : 0
                currencyAccounts[key].convertedTotalBalance += balanceOnLastDate
                currencyAccounts[key].convertedTotalTotal += convertedTotal != null ? convertedTotal : 0;
            }

            if (includeInCalculations) {
                currencyAccounts[key].totalAverage += averageSavings != null ? averageSavings : 0;
                if (accountingFx) {
                    const convertedAverage = averageSavings != null ? averageSavings / rate : 0
                    currencyAccounts[key].convertedTotalAverage += convertedAverage
                }
            }
        });
    })

    return {sections, requiredRange, currencyAccounts}
}


export function useBankingSavingsRate() {
    const mortgage = useMortgage();
    const allSections = mortgage.uploadSections
    const {sections, requiredRange,currencyAccounts} = bankStatementSummary(mortgage)

    // GET THE REQUIRED RANGE
    // Get Required Range by finding  the section that has the oldest summary.range.to.date which is less than 3 weeks old, then subtract 6 months from that date.


    // SET THE PROGRESS AS INCLUDED IN CALCULATIONS FOR SAVINGS RATE

    return {sections, requiredRange,currencyAccounts, allSections, mortgage}
}





function BankingSavingsRate(props) {
    const { requiredRange, mortgage, currencyAccounts} = useBankingSavingsRate();

    const euroAccounts = currencyAccounts['€']


    return (
        <div className="d-col gap-15">
            <Typography.Title level={4}>Savings Rate on Current & Savings Euro Accounts</Typography.Title>
            <CurrencyTable currencyAccounts={euroAccounts} mortgage={mortgage} currency={'€'} requiredRange={requiredRange}/>
            <Typography.Title level={4}>Savings Rate on Current & Savings Foreign Accounts</Typography.Title>
            {Object.keys(currencyAccounts).filter(key => key !== '€').map((key, i) => {
                const accounts = currencyAccounts[key].accounts;
                if (!accounts || !accounts.length) return null;
                return (
                    <CurrencyTable key={i} currencyAccounts={currencyAccounts[key]} mortgage={mortgage} currency={key} requiredRange={requiredRange}/>
                )
            })}
        </div>
    );
}

function CurrencyTable({currencyAccounts, mortgage, requiredRange}) {
    const handleIncludeSection = async (progressID, include) => {


        await mortgage.mutate({
            update: {
                target: `progress[${progressID}].includeInCalculations`,
                value: include,
            }
        })
    }
    const defaultEmptyProps = {
        children: null,
        props: {
            colSpan: 0,
        }
    }
    const ConversionPanel = ({origAmt,fxRate})=>{
        const converted = origAmt / fxRate
        return (
            <div className="d-col">
                <div className="d-row j-end">{origAmt.toCurrency(0)}</div>
                <div className="d-row j-end" style={{fontSize:10, color: gray[4]}}>{converted.toEuro(0)}</div>
            </div>
        )

    }
    const cols = [
        {
            title: 'Account',
            key: 'personalTitle',
            render: (row, index) => {
                const colorProps = row.difference == null ? {style: {color: red[3]}} : {};
                return (<span {...colorProps}>
                    {row.personalTitle}
                </span>)
            }
        },
        {
            title: `${requiredRange.start.format('D MMM ')} Bal`,
            key: 'openingOnDate',
            align: 'right',
            render: (row, index) => {

                if (row.openingOnDate == null) {
                    return <CloseCircleOutlined/>
                }
                if (row.convertRate){
                    return <ConversionPanel origAmt={row.openingOnDate.balance} fxRate={row.convertRate}/>
                }
                return row.openingOnDate.balance.toCurrency(0)
            },
        },
        {
            title: `${requiredRange.end.format('D MMM')} Bal`,
            key: 'closingOnDate',
            align: 'right',
            render: (row, index) => {
                if (row.closingOnDate == null) {
                    return <CloseCircleOutlined/>
                }
                if (row.convertRate){
                    return <ConversionPanel origAmt={row.closingOnDate.balance} fxRate={row.convertRate}/>
                }
                return row.closingOnDate.balance.toCurrency(0)
            },
        },
        {
            title: '6 mth Difference',
            key: 'difference',
            align: 'right',
            render: (row, index) => {
                if (row.difference == null) {
                    return <CloseCircleOutlined/>
                }
                return <ArrowedNumber value={row.difference} fxRate={row.convertRate}/>
            },
        },
        {
            title: 'Adjustments',
            key: 'adjustments',
            align: 'right',
            render: (row, index) => {
                if (row.difference == null) {
                    return <CloseCircleOutlined/>
                }
                return <ArrowedNumber value={row.adjustments} fxRate={row.convertRate}/>
            },
        },

        {
            title: 'Adjusted Total',
            key: 'total',
            align: 'right',
            render: (row, index) => {
                if (row.difference == null) {
                    return <CloseCircleOutlined/>
                }
                return <ArrowedNumber value={row.total} fxRate={row.convertRate}/>
            },
        },
        {
            title: 'Local Savings Rate',
            key: 'averageSavings',
            align: 'right',
            render: (row, index) => {

                if (row.difference == null) {
                    return <CloseCircleOutlined/>
                }
                return <ArrowedNumber value={row.averageSavings} strike={!row.includeInCalculations} fxRate={row.convertRate}/>
            },
        },
        {
            title: '€ Savings Rate',
            key: 'averageSavings',
            align: 'right',
            render: (row, index) => {

                if (row.difference == null) {
                    return <CloseCircleOutlined/>
                }

                return <ArrowedNumber value={row.convertedAverageSavings ?? row.averageSavings} strike={!row.includeInCalculations} />
            },
        },
        {
            title: 'Use',
            key: 'include',
            render: (row, index) => {
                if (!row.progressID) {
                    return {
                        ...defaultEmptyProps,
                        children: <span className="text-muted">No Progress</span>
                    }
                }
                return (
                    <Checkbox defaultChecked={row.includeInCalculations} onClick={(e) => e.stopPropagation()} onChange={(e) => {
                        handleIncludeSection(row.progressID, e.target.checked)
                    }}/>
                )
            }
        }

    ]

    const summary = pageData => {


        return (
            <>
                <Table.Summary.Row>

                    <Table.Summary.Cell index={1} colSpan={3} align="right">
                        <SummaryField value={(currencyAccounts.convertedTotalBalance ?? currencyAccounts.totalBalance ?? 0)?.toCurrency(0)} label="€ Funds"/>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} colSpan={3} align="right">
                        <SummaryField value={(currencyAccounts.convertedTotalTotal ?? currencyAccounts.totalTotal ?? 0)?.toCurrency(0)} label="€ Saved" arrow={true}/>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} colSpan={2} align="right">
                        <SummaryField value={(currencyAccounts.convertedTotalAverage ?? currencyAccounts.totalAverage ?? 0)?.toCurrency(0)} label="€/mth" arrow={true}/>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </>
        );
    }
    return   <Table summary={summary} expandable={
        {
            expandedRowRender: (record) => {
                return (
                    <SectionSavingExplanation record={record}/>
                )
            },
            expandRowByClick: true,
            showExpandColumn: false

        }
    } dataSource={currencyAccounts.accounts} columns={cols} pagination={false} rowKey="key"/>
}

function SectionUploads({record}) {
    const {openAnnotator} = useUploadAnnotator()

    const columns = [
        {
            title: 'Uploads',
            key: 'label',
            dataIndex: 'label',
        },
        {
            title: 'Queries',
            key: 'queries',
            dataIndex: 'queries',
        },
        {
            title: 'Notes',
            key: 'notes',
            dataIndex: 'notes',
            render: (value, record) => {
                if (!value || !value.length) {
                    return <span className="text-muted">No Notes</span>
                }
                return value.map((note, i) => <span key={i} className="d-row gap-5">{note}</span>)
            }
        }
    ]

    const dataSource = record.uploads.map((upload, i) => {
        const label = upload?.range?.from?.date && upload?.range?.to?.date ? `${upload.range.from.date.format('D MMM')} - ${upload.range.to.date.format('D MMM')}` : `Upload ${i + 1}`;
        const notes = []
        if (record?.requiredRange?.start?.date && upload.isDateInUpload(record.requiredRange.start.date)) {
            notes.push(`Contains ${record.requiredRange.start.date.format('D MMM')}`)
        }
        if (record?.requiredRange?.end?.date && upload.isDateInUpload(record.requiredRange.end.date)) {
            notes.push(`Contains ${record.requiredRange.end.date.format('D MMM')}`)
        }
        return {
            uploadID: upload.id,
            label,
            queries: <UploadQueries section={record} upload={upload}/>,
            notes
        }
    })
    return <Table rowHoverable dataSource={dataSource} columns={columns} pagination={false} rowKey="uploadID"
                  className="grow"
                  size="small" bordered={true} scroll={{x: 'max-content'}} onRow={
        (record) => {
            return {
                onClick: () => {
                    openAnnotator(record.uploadID)
                }
            }
        }
    }/>
}

function UploadQueries({section, upload}) {
    if (!upload || !upload.queries || !upload.queries.length) {
        return <span className="text-muted">No Queries</span>
    }

    const columns = [
        {
            title: 'Queries',
            key: 'query',
            dataIndex: 'query',
            render: (value, record) => {
                const lineThroughProps = record.linethrough ? {textDecoration: 'line-through'} : {};

                return <Typography.Paragraph style={{...lineThroughProps, maxWidth: 600}}
                                             ellipsis={{rows: 2, expandable: true}}>{value}</Typography.Paragraph>
            }
        },
        {
            title: 'Adj',
            key: 'adjustmentAmount',
            dataIndex: 'adjustmentAmount',
            align: 'right',
            render: (value, record) => {
                if (value == null) {

                    return <span className="text-muted">No Adj</span>
                }

                if (record.linethrough) {
                    return <span className="text-muted"
                                 style={{textDecoration: 'line-through'}}>{value.toCurrency(0)}</span>
                }
                if (record.adjustmentDirection === 'DOWN') {
                    return <ArrowedNumber value={-value}/>
                }
                return <ArrowedNumber value={value}/>
            }
        },
        {
            title: 'Adj List',
            key: 'adjustmentList',
            render: (value, record) => {
                if (!record.adjustmentList) {
                    return null
                }
                return (<div className="text-right">
                    {record.adjustmentList.map((item, i) => {
                        return <span key={i} className="d-row gap-5">
                           {item.toCurrency(0)}
                        </span>
                    })}
                </div>)
            }
        }
    ]
    const goodIds = section.adjustmentRows.map(it => it.queryID)
    const dataSource = upload.queries.map(query => {
        return {
            ...query,
            linethrough: !goodIds.includes(query.id),
        }
    })
    return <Table showHeader={false} upload dataSource={dataSource} columns={columns} pagination={false}
                  rowKey="uploadID"
                  size="small" bordered={true} scroll={{x: 'max-content'}}/>
}

function SectionSavingExplanation({record}) {

    let fullRange = record.fullRange ? `${record.fullRange.from.date.format('D MMM')} - ${record.fullRange.to.date.format('D MMM')}` : 'No Range';
    return (
        <>
            <span className="ms-9"> Full Range: {fullRange}</span>
            <div className="d-row gap-15">
                <SectionUploads record={record}/>
            </div>
        </>

    )
}

export default BankingSavingsRate;